import { useState } from "react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import type { FindingQueueQuery } from "@iamu/shared";

interface BulkCreateIssuesDialogProps {
  projectId: string;
  canonicalIds?: string[];
  query?: FindingQueueQuery;
  parentLinkId?: string;
  hotspotKey?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SKIP_LABEL: Record<string, string> = {
  dismissed: "dismissed in iamu",
  not_problem: "strengths",
  verified: "verified fixed",
};

/**
 * Bulk "Create issues" with a dedupe preview: what will be created, what
 * already has an issue (with its key), what is skipped. Supports manual
 * selection or Top-N by priority from a queue filter.
 */
export function BulkCreateIssuesDialog({
  projectId,
  canonicalIds,
  query,
  parentLinkId,
  hotspotKey,
  open,
  onOpenChange,
}: BulkCreateIssuesDialogProps) {
  const [queued, setQueued] = useState(false);
  const [topN, setTopN] = useState<number>(20);

  const previewInput = query
    ? { projectId, query, topN, parentLinkId, hotspotKey }
    : {
        projectId,
        canonicalIds: canonicalIds ? canonicalIds.slice(0, 200) : [],
        parentLinkId,
        hotspotKey,
      };

  const preview = trpc.workflow.bulkPreview.useQuery(previewInput, {
    enabled:
      open &&
      (Boolean(query) ||
        (canonicalIds !== undefined && canonicalIds.length > 0)),
    refetchInterval: (q) =>
      queued &&
      q.state.data?.rows.some(
        (r) => r.status === "pending" || r.status === "create",
      )
        ? 3000
        : false,
  });
  const utils = trpc.useUtils();
  const create = trpc.workflow.bulkCreate.useMutation({
    onSuccess: (res) => {
      setQueued(true);
      toast.success(
        res.queued === 1
          ? "Creating 1 issue in the background"
          : `Creating ${res.queued} issues in the background (about ${res.queued} s)`,
      );
      utils.workflow.bulkPreview.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to create issues"),
  });

  const rows = preview.data?.rows ?? [];
  const toCreate = rows.filter((r) => r.status === "create");
  const linked = rows.filter((r) => r.status === "linked");
  const pending = rows.filter((r) => r.status === "pending");
  const skipped = rows.filter((r) => r.status === "skip");
  const failed = toCreate.filter((r) => r.failed);
  const skipCounts = skipped.reduce<Record<string, number>>((acc, r) => {
    const k = r.skipReason ?? "dismissed";
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  const blocked = !preview.data?.configured
    ? "No issue tracker is connected on this server."
    : !preview.data?.target
      ? "Link an issue tracker (Jira, Linear, GitHub, etc.) to this project to create issues."
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create issues</DialogTitle>
          <DialogDescription>
            {preview.data?.target
              ? `One issue per finding in ${preview.data.target}. Findings that already have an open issue are skipped.`
              : "One issue per finding. Findings that already have an open issue are skipped."}
          </DialogDescription>
        </DialogHeader>
        {preview.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="space-y-3 text-sm">
            {query && (
              <div className="flex items-center justify-between gap-2 border-b pb-3">
                <span className="text-sm font-medium">Batch size:</span>
                <div
                  className="inline-flex rounded-md border p-0.5"
                  role="group"
                  aria-label="Top findings count"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <Button
                      key={n}
                      type="button"
                      size="sm"
                      variant={topN === n ? "secondary" : "ghost"}
                      className="h-7 px-2.5 text-xs font-semibold"
                      onClick={() => setTopN(n)}
                    >
                      Top {n}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <p>
              <output className="font-semibold tabular-nums">
                {query
                  ? (preview.data?.rows.length ?? 0)
                  : (canonicalIds?.length ?? 0)}
              </output>{" "}
              {query ? "top findings evaluated by priority" : "selected"}
            </p>
            <ul className="space-y-1">
              <li>
                <span className="font-medium tabular-nums">
                  {toCreate.length}
                </span>{" "}
                will be created
                {failed.length > 0 && (
                  <span className="text-muted-foreground">
                    {" "}
                    ({failed.length} retried after a failure)
                  </span>
                )}
              </li>
              {pending.length > 0 && (
                <li>
                  <span className="font-medium tabular-nums">
                    {pending.length}
                  </span>{" "}
                  being created now
                </li>
              )}
              {linked.length > 0 && (
                <li>
                  <span className="font-medium tabular-nums">
                    {linked.length}
                  </span>{" "}
                  already have an issue:{" "}
                  <span className="text-muted-foreground">
                    {linked
                      .slice(0, 6)
                      .map((r) => r.displayKey)
                      .join(", ")}
                    {linked.length > 6 ? ", …" : ""}
                  </span>
                </li>
              )}
              {Object.entries(skipCounts).map(([k, n]) => (
                <li key={k}>
                  <span className="font-medium tabular-nums">{n}</span> skipped:{" "}
                  {SKIP_LABEL[k] ?? k}
                </li>
              ))}
            </ul>
            {blocked && <p className="text-muted-foreground">{blocked}</p>}
            {queued && toCreate.length === 0 && pending.length === 0 && (
              <p role="status">All issues are created.</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {queued ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={() =>
              create.mutate(
                query
                  ? { projectId, query, topN, parentLinkId, hotspotKey }
                  : {
                      projectId,
                      canonicalIds: canonicalIds ?? [],
                      parentLinkId,
                      hotspotKey,
                    },
              )
            }
            disabled={
              !!blocked || toCreate.length === 0 || create.isPending || queued
            }
          >
            {toCreate.length === 1
              ? "Create 1 issue"
              : `Create ${toCreate.length} issues`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
